/**
 * TypeScript types for Partner Access Management
 * Corresponds to backend/Models/PartnerAccessModels.cs
 */

// ==================== Enums ====================

export enum TokenStatus {
  Active = 'Active',
  Revoked = 'Revoked',
  Expired = 'Expired',
  Pending = 'Pending'
}

export enum AssignmentStatus {
  Active = 'Active',
  Revoked = 'Revoked',
  Expired = 'Expired',
  Pending = 'Pending'
}

export enum GameSource {
  Global = 'Global',
  Local = 'Local'
}

export enum DisplayMode {
  Full = 'FULL',
  Anon = 'ANON',
  None = 'NONE'
}

// ==================== Core Models ====================

export interface AccessToken {
  tokenId: number;
  tokenName: string;
  description?: string;
  partnerId: number;
  partnerName?: string;
  createdByUserEmail: string;
  status: TokenStatus;
  createdAt: string;
  expiresAt?: string;
  lastUsedAt?: string;
  usageCount: number;
  assignmentCount: number;
  allowedIpAddresses?: string;
  rateLimitPerHour?: number;
  revokedAt?: string;
  revokedByUserEmail?: string;
  revocationReason?: string;
}

export interface GamePartnerAssignment {
  id: number;
  accessTokenId: number;
  partnerId: number;
  partnerName?: string;
  gameSource: GameSource;
  globalGameId?: number;
  localGameId?: number;
  gameDescription?: string;
  displayMode: DisplayMode;
  canRead: boolean;
  canUpdate: boolean;
  allowedUpdateFields?: string[];
  status: AssignmentStatus;
  createdAt: string;
  createdByUserId: string;
  updatedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  revokedByUserId?: string;
  revocationReason?: string;
  notes?: string;
}

export interface PartnerAccessAudit {
  id: number;
  accessTokenId: number;
  partnerId: number;
  partnerName?: string;
  action: string;
  endpoint: string;
  ipAddress?: string;
  userAgent?: string;
  requestData?: string;
  responseStatus: number;
  errorMessage?: string;
  timestamp: string;
  durationMs: number;
}

// ==================== Request Models ====================

export interface CreatePartnerTokenRequest {
  partnerId: number;
  tokenName: string;
  tokenDescription?: string;
  expiresAt?: string;
  allowedIpAddresses?: string;
  rateLimitPerHour?: number;
  displayMode: DisplayMode;
  canUpdate: boolean;
  allowedUpdateFields?: string[];
}

export interface CreatePartnerAccessRequest {
  tokenName: string;
  tokenDescription?: string;
  partnerId: number;
  gameSource: GameSource;
  globalGameId?: number;
  localGameId?: number;
  displayMode: DisplayMode;
  canUpdate: boolean;
  allowedUpdateFields?: string[];
  expiresAt?: string;
  allowedIpAddresses?: string;
  rateLimitPerHour?: number;
  notes?: string;
}

export interface UpdateAssignmentRequest {
  displayMode?: DisplayMode;
  canUpdate?: boolean;
  allowedUpdateFields?: string[];
  expiresAt?: string;
  notes?: string;
}

export interface RevokeTokenRequest {
  reason: string;
}

export interface RevokeAssignmentRequest {
  reason: string;
}

// ==================== Response Models ====================

export interface CreatePartnerTokenResponse {
  tokenId: number;
  token: string; // Plaintext token - only shown once!
  tokenName: string;
  partnerId: number;
  partnerName: string;
  displayMode: DisplayMode;
  canUpdate: boolean;
  allowedUpdateFields?: string;
  createdAt: string;
  expiresAt?: string;
  status: TokenStatus;
}

export interface CreatePartnerAccessResponse {
  token: AccessToken;
  assignment: GamePartnerAssignment;
  plaintextToken: string;
}

export interface PartnerAccessStatsResponse {
  totalTokens: number;
  activeTokens: number;
  revokedTokens: number;
  expiredTokens: number;
  totalAssignments: number;
  activeAssignments: number;
  totalApiCalls: number;
  apiCallsLast24h: number;
  apiCallsLast7d: number;
  topPartners: PartnerUsageStats[];
  recentActivity: PartnerAccessAudit[];
}

export interface PartnerUsageStats {
  partnerId: number;
  partnerName: string;
  tokenCount: number;
  assignmentCount: number;
  apiCallCount: number;
  lastUsed?: string;
}

// ==================== UI Helper Types ====================

export interface TokenWithAssignments extends AccessToken {
  assignments: GamePartnerAssignment[];
}

export interface GameOption {
  id: number;
  source: GameSource;
  description: string;
  date?: string;
}

// ==================== Display Labels ====================

export const TokenStatusLabels: Record<TokenStatus, string> = {
  [TokenStatus.Active]: 'Aktiv',
  [TokenStatus.Revoked]: 'Widerrufen',
  [TokenStatus.Expired]: 'Abgelaufen',
  [TokenStatus.Pending]: 'Ausstehend'
};

export const TokenStatusColors: Record<TokenStatus, string> = {
  [TokenStatus.Active]: '#28a745',
  [TokenStatus.Revoked]: '#dc3545',
  [TokenStatus.Expired]: '#6c757d',
  [TokenStatus.Pending]: '#ffc107'
};

export const AssignmentStatusLabels: Record<AssignmentStatus, string> = {
  [AssignmentStatus.Active]: 'Aktiv',
  [AssignmentStatus.Revoked]: 'Widerrufen',
  [AssignmentStatus.Expired]: 'Abgelaufen',
  [AssignmentStatus.Pending]: 'Ausstehend'
};

export const AssignmentStatusColors: Record<AssignmentStatus, string> = {
  [AssignmentStatus.Active]: '#28a745',
  [AssignmentStatus.Revoked]: '#dc3545',
  [AssignmentStatus.Expired]: '#6c757d',
  [AssignmentStatus.Pending]: '#ffc107'
};

export const DisplayModeLabels: Record<DisplayMode, string> = {
  [DisplayMode.Full]: 'Vollständig',
  [DisplayMode.Anon]: 'Anonymisiert',
  [DisplayMode.None]: 'Keine Anzeige'
};

export const DisplayModeColors: Record<DisplayMode, string> = {
  [DisplayMode.Full]: '#007bff',
  [DisplayMode.Anon]: '#ffc107',
  [DisplayMode.None]: '#6c757d'
};

export const GameSourceLabels: Record<GameSource, string> = {
  [GameSource.Global]: 'Globales Spiel',
  [GameSource.Local]: 'Lokales Spiel'
};

// ==================== Allowed Update Fields ====================

export const AllowedUpdateFieldOptions = [
  { value: 'BoardingStatus', label: 'Boarding-Status' },
  { value: 'RoomAssignment', label: 'Zimmerzuweisung' },
  { value: 'SeatAssignment', label: 'Sitzplatzzuweisung' },
  { value: 'MealPreference', label: 'Essenspräferenz' },
  { value: 'SpecialRequests', label: 'Sonderwünsche' },
  { value: 'CheckInStatus', label: 'Check-In-Status' },
  { value: 'TransportStatus', label: 'Transport-Status' },
  { value: 'Notes', label: 'Notizen' }
];

